import React from 'react'
import { Button, Result, Table, Tag, Space, Popconfirm } from 'antd'
import { useDispatch } from 'react-redux'
import { useFetchUsersQuery, useReinviteUserMutation } from '../../../../../store/apis/user.api'
import {
    EditOutlined,
    MinusCircleOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    MailOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons'
import UserRoles, { getRoleName } from '../../../../../constants/UserRoles'
import UserStatus, { getUserStatusText } from '../../../../../constants/UserStatus'
import useQueryParam from '../../../../../hooks/useQueryParam'
import { USER_SEARCH_PARAM } from '../../../../../constants/query-params'
import { userActions } from '../../../../../store/slices/user.slice'
import { useFetchOrganizationsQuery } from '../../../../../store/apis/organization.api'
import { useIsSenseUserOrImpersonated } from '../../../../../hooks/useIsSenseUserOrImpersonated'
import { getVisibleRoles, getVisibleRoleFilterOptions } from '../../../../../utils/rolePermissions.helper'
import { isNil } from 'lodash'
import useAuthorization from '../../../../../components/authorization/useAuthorization'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import { useThemeColors } from '../../../../../hooks/useThemeColors'
import './users-table.scss'

const UsersTable = () => {
    const colors = useThemeColors()
    const dispatch = useDispatch()
    const searchParam = useQueryParam(USER_SEARCH_PARAM)

    const isSenseUserOrImpersonated = useIsSenseUserOrImpersonated()
    const { isAuthorized: isSystemAdmin } = useAuthorization([UserRoles.SYSTEM_ADMIN])
    const { isAuthorized: isAdmin } = useAuthorization([UserRoles.ADMIN])
    const { isAuthorized: isUserManager } = useAuthorization([UserRoles.USER_MANAGER])

    // Sense users (Admin/User Manager) and System Admins can view all users across organizations
    // External org users (Admin/User Manager) can only view users from their own organization
    const canSeeAllUsers = isSystemAdmin || (isSenseUserOrImpersonated && (isAdmin || isUserManager))

    const { data, isFetching, error, refetch } = useFetchUsersQuery({ organizationOnly: !canSeeAllUsers })
    const { data: organizations, isFetching: isFetchingOrganizations } = useFetchOrganizationsQuery(null, {
        skip: !canSeeAllUsers,
    })
    const [reinviteUser, reinviteUserResult] = useReinviteUserMutation()

    const handleReinvite = async (userId, userEmail) => {
        try {
            await reinviteUser({ userId }).unwrap()
            openNotificationWithIcon(
                'success',
                `Invitation resent to ${userEmail}. The user will receive a new invitation email with an extended expiry date.`,
            )
        } catch (error) {
            console.error(error)
            openNotificationWithIcon('error', 'Failed to resend invitation. Please try again.')
        }
    }

    // Get visible role options for filters using the helper
    const visibleRoleOptions = getVisibleRoleFilterOptions(isSenseUserOrImpersonated)

    const columns = [
        {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
            sorter: (a, b) => a.name?.length - b.name?.length,
        },
        {
            title: 'Email',
            key: 'email',
            dataIndex: 'email',
        },
        ...(canSeeAllUsers
            ? [
                  {
                      title: 'Organization',
                      key: 'organization',
                      dataIndex: 'organization',
                      filters: [
                          ...(organizations?.map(organization => ({
                              text: organization.name,
                              value: organization.id,
                          })) || []),
                      ],
                      onFilter: (value, record) =>
                          record.organization === organizations?.find(organization => organization.id === value)?.name,
                      filterSearch: true,
                  },
              ]
            : []),
        {
            title: 'Roles',
            key: 'roles',
            dataIndex: 'roles',
            render: (roles, record) => {
                const isTargetUserFromSense = record.email?.endsWith('@sense.network')
                const visibleRoles = getVisibleRoles(roles, isSenseUserOrImpersonated, isTargetUserFromSense)
                return visibleRoles.map(role => getRoleName(role)).join(', ')
            },
            filters: visibleRoleOptions,
            onFilter: (value, record) => {
                const isTargetUserFromSense = record.email?.endsWith('@sense.network')
                const visibleRoles = getVisibleRoles(record.roles, isSenseUserOrImpersonated, isTargetUserFromSense)
                return visibleRoles.includes(value)
            },
        },
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            render: (status, record) => {
                switch (status) {
                    case UserStatus.ACTIVE:
                        return (
                            <Tag icon={<CheckCircleOutlined />} color='success'>
                                Active
                            </Tag>
                        )
                    case UserStatus.DISABLED:
                        return (
                            <Tag icon={<MinusCircleOutlined />} color='default'>
                                Disabled
                            </Tag>
                        )
                    case UserStatus.PENDING:
                        if (record.invitationExpired) {
                            return (
                                <Tag icon={<ExclamationCircleOutlined />} color='error'>
                                    Invitation Expired
                                </Tag>
                            )
                        }
                        return (
                            <Tag icon={<ClockCircleOutlined />} color='warning'>
                                Pending
                            </Tag>
                        )
                    default:
                        return <Tag color='default'>{getUserStatusText(status)}</Tag>
                }
            },
            filters: [
                { text: 'Active', value: UserStatus.ACTIVE },
                { text: 'Disabled', value: UserStatus.DISABLED },
                { text: 'Pending', value: UserStatus.PENDING },
                { text: 'Invitation Expired', value: 'EXPIRED' },
            ],
            onFilter: (value, record) => {
                if (value === 'EXPIRED') {
                    return record.status === UserStatus.PENDING && record.invitationExpired
                }
                return record.status === value
            },
            sorter: (a, b) => a.status - b.status,
        },
        {
            title: 'Actions',
            key: 'actions',
            dataIndex: 'actions',
            fixed: 'right',
            width: 80,
            className: 'fixed-actions-column',
            render: (_, record) => (
                <Space>
                    <Button
                        type='text'
                        icon={<EditOutlined style={{ color: colors.primaryText }} />}
                        shape='circle'
                        title='Edit User'
                        onClick={() => dispatch(userActions.setEditingUserId(record.key))}
                    />
                    {record.status === UserStatus.PENDING && (
                        <Popconfirm
                            title={record.invitationExpired ? 'Resend Expired Invitation' : 'Resend Invitation'}
                            description={`Are you sure you want to resend the invitation to ${record.email}? This will create a new invitation token and extend the expiry date.`}
                            onConfirm={() => handleReinvite(record.key, record.email)}
                            okText='Yes'
                            cancelText='No'
                            disabled={reinviteUserResult.isLoading}
                        >
                            <Button
                                type='text'
                                icon={<MailOutlined style={{ color: record.invitationExpired ? colors.error : colors.primaryText }} />}
                                shape='circle'
                                title={record.invitationExpired ? 'Resend Expired Invitation' : 'Resend Invitation'}
                                loading={reinviteUserResult.isLoading}
                            />
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ]

    let adaptedData =
        data?.map(user => {
            let organizationName = 'Unknown organization'
            if (isFetchingOrganizations) organizationName = '...'
            else if (organizations) {
                const organization = organizations.find(organization => organization.id === user.organizationId)
                if (organization) organizationName = organization.name
            } else if (isNil(user.organizationId)) organizationName = 'No organization'

            // Map legacy enabled property to status if status is not present
            const status = user.status || (user.enabled ? UserStatus.ACTIVE : UserStatus.DISABLED)

            return {
                key: user.id,
                name: user.name,
                email: user.email,
                roles: user.roles,
                organization: organizationName,
                status: status,
                invitationExpired: user.invitationExpired || false,
                tokenExpiresAt: user.tokenExpiresAt,
            }
        }) || []

    if (searchParam)
        adaptedData = adaptedData.filter(
            user =>
                user.name?.toLowerCase()?.includes(searchParam.toLowerCase()) ||
                user.email?.toLowerCase()?.includes(searchParam.toLowerCase()),
        )

    return error ? (
        <Result
            status='error'
            title='Users Fetch Failed'
            subTitle={error}
            extra={[
                <Button key='try' onClick={() => refetch()} loading={isFetching}>
                    Try Again
                </Button>,
            ]}
        />
    ) : (
        <Table
            columns={columns}
            dataSource={adaptedData}
            pagination={false}
            loading={isFetching}
            scroll={{ x: true }}
        />
    )
}

export default UsersTable
